import React from 'react';
import type { SnapPoint } from '@/types/alignment';

interface AlignmentGuidesOverlayProps {
  snapPoints: SnapPoint[];
  viewport: {
    scale: number;
    positionX: number;
    positionY: number;
  };
  containerWidth: number;
  containerHeight: number;
  formatMeasurement: (pixels: number) => string;
}

/**
 * Renders visual alignment guides during dragging
 * Shows snap lines when elements are aligned
 */
export function AlignmentGuidesOverlay({
  snapPoints,
  viewport,
  containerWidth,
  containerHeight,
  formatMeasurement,
}: AlignmentGuidesOverlayProps) {
  if (!snapPoints || snapPoints.length === 0) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        zIndex: 999, // Above all elements but below controls
      }}
    >
      <svg
        width={containerWidth}
        height={containerHeight}
        className="absolute inset-0"
        style={{ overflow: 'visible' }}
      >
        {snapPoints.map((snapPoint, index) => {
          const { point, axis, type, distance } = snapPoint;

          // Convert image coordinates to screen coordinates
          const screenX = point.x * viewport.scale + viewport.positionX;
          const screenY = point.y * viewport.scale + viewport.positionY;

          // Determine line color based on snap type
          const strokeColor = type === 'center' ? '#3b82f6' : type === 'distributed' ? '#f59e0b' : '#10b981'; // blue for center, amber for distributed, green for edge
          const strokeWidth = type === 'center' ? 2 : 1.5;

          // Format the distance label
          const distanceLabel = formatMeasurement(distance);

          // Only show distance label for distributed spacing (orange guides)
          // Regular snap guides (blue/green) are just for alignment, not spacing
          const showDistance = type === 'distributed' && distance > 1;

          if (axis === 'x') {
            // Vertical line for x-axis alignment
            return (
              <g key={`snap-x-${index}`}>
                <line
                  x1={screenX}
                  y1={0}
                  x2={screenX}
                  y2={containerHeight}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray="4 4"
                  opacity={0.7}
                  style={{
                    transition: 'opacity 0.1s ease-out',
                  }}
                />
                {showDistance && (
                  <>
                    {/* Background pill for text */}
                    <rect
                      x={screenX - 22}
                      y={containerHeight / 2 - 20}
                      width={44}
                      height={16}
                      rx={8}
                      fill={strokeColor}
                      opacity={0.9}
                    />
                    {/* Distance text */}
                    <text
                      x={screenX}
                      y={containerHeight / 2 - 12}
                      fill="white"
                      fontSize="11"
                      fontWeight="500"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        pointerEvents: 'none',
                      }}
                    >
                      {distanceLabel}
                    </text>
                  </>
                )}
              </g>
            );
          } else if (axis === 'y') {
            // Horizontal line for y-axis alignment
            return (
              <g key={`snap-y-${index}`}>
                <line
                  x1={0}
                  y1={screenY}
                  x2={containerWidth}
                  y2={screenY}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray="4 4"
                  opacity={0.7}
                  style={{
                    transition: 'opacity 0.1s ease-out',
                  }}
                />
                {showDistance && (
                  <>
                    {/* Background pill for text */}
                    <rect
                      x={containerWidth / 2 - 22}
                      y={screenY - 8}
                      width={44}
                      height={16}
                      rx={8}
                      fill={strokeColor}
                      opacity={0.9}
                    />
                    {/* Distance text */}
                    <text
                      x={containerWidth / 2}
                      y={screenY}
                      fill="white"
                      fontSize="11"
                      fontWeight="500"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        pointerEvents: 'none',
                      }}
                    >
                      {distanceLabel}
                    </text>
                  </>
                )}
              </g>
            );
          }

          return null;
        })}
      </svg>
    </div>
  );
}
